import { Account } from '@/domain/usecases'
import { UserEnrollmentContentInfo } from '@/domain/usecases/content/enrollment/user-enrollment-content-info'
import { findRouteByPathname } from '@/main/routes/paths'
import { settings } from '@/main/config/settings'
import { FeatureFlagClient } from '@/infra/feature-flag'

export type GenericTrackingData = {
  timestamp: string
  user_id: string | null
  user_email: string | null
  user_account_type: string | null
  user_name: string | null
  user_language: string | null
  user_roles: string[] | null
  user_company_id: string | null
  user_company_name: string | null
  user_onboarding_completed: boolean | null
  user_pass_plan_id: number | string | null
  current_page: string
  current_page_title: string | null
  previous_page: string | null
  user_environment: string
  experiments: Record<string, unknown>
  enrollment_info?: {
    restricted: boolean
    restriction_type: string | null
  }
}

let account: Account.Model | null = null
let previousPathname: string | null = null
let currentPathname: string | null = null

const buildUserData = (account: Account.Model | null) => ({
  user_id: account?.id ?? null,
  user_email: account?.email ?? null,
  user_account_type: account?.accountType?.name ?? null,
  user_name: account?.name ?? null,
  user_language: account?.preferenceLanguage ?? null,
  user_roles: account?.roles?.map(role => role.name) ?? null,
  user_company_id: account?.company?.uniqueId ?? null,
  user_company_name: account?.company?.name ?? null,
  user_onboarding_completed: account?.onboardingCompleted ?? null,
  user_pass_plan_id: account?.passPlan?.id ?? null,
  user_pass_plan_name: account?.passPlan?.name ?? null,
  user_picture_url: account?.pictureUrl ?? null,
  user_addons_refund_enabled: account?.addons?.refund.enabled ?? null,
  user_onboarding_completed_at: account?.onboardingCompletedAt
})

const buildNavigationData = (currentPathname: string, previousPathname?: string | null) => {
  const currentRoute = findRouteByPathname(currentPathname)
  const previousRoute = previousPathname ? findRouteByPathname(previousPathname) : null

  return {
    current_page: currentRoute?.name ?? currentPathname,
    current_page_title: currentRoute?.title ?? null,
    previous_page: previousRoute?.name ?? previousPathname ?? null
  }
}

const buildEnrollmentInfo = (
  userEnrollmentContentInfo?: UserEnrollmentContentInfo.Model | null
) => {
  if (!userEnrollmentContentInfo) return undefined

  return {
    restricted: userEnrollmentContentInfo.restricted,
    restriction_type: userEnrollmentContentInfo.restrictionType?.name ?? null
  }
}

export const AccountStore = {
  setCurrentAccount: (newAccount: Account.Model | null): void => {
    account = newAccount
  },

  getCurrentAccount: (): Account.Model | null => account,

  clearAccount: (): void => {
    account = null
  },

  updateNavigation: (newPathname: string): void => {
    previousPathname = currentPathname
    currentPathname = newPathname
  },

  buildGenericTrackingData(
    userEnrollmentContentInfo?: UserEnrollmentContentInfo.Model | null,
    pathname?: string,
    previousPath?: string | null
  ): GenericTrackingData {
    const enrollmentInfo = buildEnrollmentInfo(userEnrollmentContentInfo)

    // Use stored previous pathname if not explicitly provided
    const effectivePreviousPath = previousPath ?? previousPathname
    const effectiveCurrentPath = pathname ?? currentPathname ?? window.location.pathname

    return {
      timestamp: new Date().toISOString(),
      ...buildUserData(AccountStore.getCurrentAccount()),
      ...buildNavigationData(effectiveCurrentPath, effectivePreviousPath),
      user_environment: settings.APP_ENV,
      experiments: FeatureFlagClient.getExperiments(),
      ...(enrollmentInfo && { enrollment_info: enrollmentInfo })
    }
  }
}
